import Image from 'next/image';
import Link from 'next/link';
import Logo from '@/components/brand/Logo';
import TextLink from '@/components/ui/TextLink';
import Reveal from '@/components/motion/Reveal';
import SignOutPrompt from '@/components/auth/SignOutPrompt';

/**
 * Two-column auth layout from the Figma onboarding frames (176:15834, 176:15922).
 *
 * The 1440px frame splits 756 / 684. The right panel is a photograph inset 16px
 * with the marketing copy over it — the copy is real text rather than part of
 * the image, so it stays selectable, translatable and readable by a screen
 * reader.
 *
 * The form column fades up on arrival. Sign-in is the first thing anyone sees
 * of the product, and it is the one screen where a beat of motion buys
 * something: it covers the gap between the shell painting and the fields
 * being ready, which otherwise reads as a flash of empty layout.
 */

function ProductPanel() {
  return (
    <aside className="hidden p-4 lg:block lg:w-[47.5%]">
      <div className="relative h-full w-full overflow-hidden rounded-2xl bg-brand">
        <Image
          src="/brand/auth-panel.jpg"
          alt=""
          fill
          priority
          sizes="(min-width: 1024px) 47.5vw, 0px"
          className="object-cover"
        />
        {/* Keeps the copy legible over the lighter top-right of the photograph. */}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 flex flex-col items-start p-12 text-white">
          <p className="mb-5 rounded-full bg-white/12 px-3.5 py-1.5 text-xs font-medium ring-1 ring-white/20 backdrop-blur-md">
            Multi-currency for global business
          </p>
          <h2 className="max-w-[420px] text-4xl font-semibold">
            One account to collect, hold and pay across borders.
          </h2>
          <p className="mt-5 max-w-[440px] text-base leading-6 text-white/75">
            USD, GBP and EUR virtual accounts. Local payouts, payroll and FX, all from one console.
          </p>
        </div>
      </div>
    </aside>
  );
}

/**
 * @param {'sign-in'|'sign-up'|'signed-in'} [mode]
 *   `signed-in` is for the screens `AuthShell` serves to somebody who already
 *   has a session — the business picker, chiefly. They get "Not you? Sign
 *   out" instead of an invitation to create the account they are signed in
 *   to.
 */
export default function AuthShell({ children, mode = 'sign-in' }) {
  const isSignIn = mode === 'sign-in';
  const isSignedIn = mode === 'signed-in';

  return (
    <main className="flex min-h-screen bg-surface">
      <section className="flex w-full flex-col lg:w-[52.5%]">
        <header className="flex h-9 shrink-0 items-center justify-between px-8 pt-10 sm:px-14">
          <Link href="/" aria-label="InversePay — home">
            <Logo className="h-9 w-auto" />
          </Link>
          {isSignedIn ? (
            <SignOutPrompt />
          ) : (
            <p className="text-sm text-muted">
              {isSignIn ? 'Need an account?' : 'Already have an account?'}{' '}
              <TextLink href={isSignIn ? '/auth/sign-up' : '/auth/sign-in'}>
                {isSignIn ? 'Sign up' : 'Sign in'}
              </TextLink>
            </p>
          )}
        </header>

        <div className="flex flex-1 items-center px-8 py-10 sm:px-14">
          {/* 420px, not 520 — a login form wider than this leaves the labels
              stranded a long way from their fields. */}
          {/* Reveal is a client component; `children` are already-constructed
              elements passed through as a prop, so this does not pull the auth
              forms across the server/client boundary. */}
          <Reveal className="mx-auto w-full max-w-[420px]">{children}</Reveal>
        </div>
      </section>

      <ProductPanel />
    </main>
  );
}
